import type { ChangeEvent, MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { Checkbox } from '@gto/ui/checkbox'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import { useAccountPartialUpdate, useAccountRetrieve } from '@/api/gen'
import { FormContainer } from '@/components/form-container'
import { Loader } from '@/components/loader'
import { TextInput } from '@/components/text-input'
import { useFormValidation } from '@/hooks/use-form-validation'
import { getAxiosErrorMessage } from '@/utils/helpers'
import type { SettingsFormField } from '@/utils/validation'
import { type SettingsFormData, settingsSchema } from '@/utils/validation'

export const SettingsForm = () => {
  const { data, isFetching } = useAccountRetrieve({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      enabled: true,
      placeholderData: keepPreviousData,
    },
  })

  const { validate, validateField, errors, clearFieldError, isValid } =
    useFormValidation(settingsSchema)

  const [updateStatus, setUpdateStatus] = useState<{
    status?: 'success' | 'warning'
    message?: string
  } | null>(null)
  const [formValues, setFormValues] = useState<SettingsFormData>({
    whatsapp_number: '',
    discord_tag: '',
    telegram_contact: '',
    allow_marketing_emails: false,
  })

  const { mutate: updateAccount, isPending } = useAccountPartialUpdate({
    mutation: {
      onSuccess: (data) => {
        setUpdateStatus({
          status: 'success',
          message: 'Settings fields were updated successfully',
        })
        setFormValues(data)

        setTimeout(() => {
          setUpdateStatus(null)
        }, 5000)
      },
      onError: (error: AxiosError) => {
        const errorMessage = getAxiosErrorMessage(error)
        setUpdateStatus({
          status: 'warning',
          message: errorMessage || 'An error occurred while updating settings',
        })
      },
    },
  })
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setFormValues((prev) => ({ ...prev, [name]: value }))

    if (value.trim().length) {
      setTimeout(() => {
        if (validateField(name as SettingsFormField, value)) {
          clearFieldError(name as SettingsFormField)
        }
      }, 300)
    } else {
      clearFieldError(name as SettingsFormField)
    }
  }

  const onCheckboxChange = (name: SettingsFormField, checked: boolean) => {
    setFormValues((prev) => ({ ...prev, [name]: checked }))
    setTimeout(() => {
      if (validateField(name, checked)) {
        clearFieldError(name)
      }
    }, 300)
  }

  const onSubmit = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setUpdateStatus(null)
    const isValid = validate(formValues)
    if (isValid) {
      updateAccount({ data: formValues })
    }
  }
  useEffect(
    function syncFormValuesFromAccountData() {
      if (data) {
        setFormValues({
          whatsapp_number: data.whatsapp_number,
          discord_tag: data.discord_tag,
          telegram_contact: data.telegram_contact,
          allow_marketing_emails: data.allow_marketing_emails,
        })
      }
    },
    [data],
  )

  if (isFetching) {
    return <Loader />
  }

  return (
    <Paper className="rounded-md border border-divider p-8 sm:p-4">
      {' '}
      <FormContainer>
        <Typography
          gutterBottom
          variant="h5"
        >
          <FormattedMessage id="user.settings.instantMessengers" />
        </Typography>

        <div className="flex flex-col gap-2">
          <TextInput
            error={errors.whatsapp_number}
            label="common.labels.whatsappNumber"
            input={{
              type: 'tel',
              value: formValues.whatsapp_number ?? '',
              size: 'medium',
              onChange,
              name: 'whatsapp_number',
              disabled: isPending,
            }}
          />
          <TextInput
            error={errors.discord_tag}
            label="common.labels.discordId"
            input={{
              type: 'text',
              value: formValues.discord_tag ?? '',
              size: 'medium',
              onChange,
              name: 'discord_tag',
              disabled: isPending,
            }}
          />
          <TextInput
            error={errors.telegram_contact}
            label="common.labels.telegram"
            input={{
              type: 'tel',
              value: formValues.telegram_contact ?? '',
              size: 'medium',
              onChange,
              name: 'telegram_contact',
              disabled: isPending,
            }}
          />
        </div>

        <Typography
          gutterBottom
          variant="h5"
        >
          <FormattedMessage id="user.settings.marketingCommunications" />
        </Typography>
        <Checkbox
          checked={Boolean(formValues.allow_marketing_emails)}
          label={<FormattedMessage id="user.settings.marketingCommunications.description" />}
          onCheckedChange={(checked) => onCheckboxChange('allow_marketing_emails', checked)}
        />

        <div className="flex flex-col gap-4">
          {updateStatus && (
            <Alert
              heading={updateStatus.message}
              severity={updateStatus.status}
            />
          )}
          <Button
            disabled={!isValid || isPending}
            loading={isPending}
            size="large"
            type="submit"
            variant="contained"
            onClick={onSubmit}
          >
            <FormattedMessage id="common.controls.saveChanges" />
          </Button>
        </div>
      </FormContainer>
    </Paper>
  )
}
